import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Flag, Award, MapPin, ChevronRight } from "lucide-react";

/**
 * Ablauf-/Expiry-Übersicht für das Web. Spiegelt den mobilen
 * ExpiryOverviewScreen 1:1. Die mobile RN-Logik (`@client/lib/expiryWarnings`)
 * hängt an AsyncStorage + expo-notifications, daher replizieren wir hier nur
 * die reine Datums-/Schwellenwert-Logik und füttern sie mit den über React
 * Query geladenen Daten (`/api/profile`, `/api/drones`).
 */

type ExpiryCategory = "alphaTango" | "license" | "allgemeinerlaubnis";
type ExpiryStatus = "ok" | "info" | "warn" | "critical" | "expired" | "missing";

interface ExpiryItem {
  id: string;
  category: ExpiryCategory;
  label: string;
  subtitle?: string;
  rawDate?: string;
  expiresAt: Date | null;
  daysUntilExpiry: number | null;
  status: ExpiryStatus;
}

const STATUS_COLOR: Record<ExpiryStatus, string> = {
  expired: "#FF3B30",
  critical: "#FF453A",
  warn: "#FF9F0A",
  info: "#FFD60A",
  missing: "#8E8E93",
  ok: "#30D158",
};

const STATUS_LABEL: Record<ExpiryStatus, string> = {
  expired: "Abgelaufen",
  critical: "Letzter Monat",
  warn: "Vorletzter Monat",
  info: "Drei Monate",
  missing: "Kein Datum",
  ok: "Gültig",
};

// ───────────────────────────── Datums-Logik (RN-frei) ──────────────────────

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function makeValidatedDate(year: number, monthIdx: number, day: number): Date | null {
  if (year < 1900 || year > 2200) return null;
  if (monthIdx < 0 || monthIdx > 11) return null;
  if (day < 1 || day > 31) return null;
  const d = new Date(year, monthIdx, day);
  if (
    isNaN(d.getTime()) ||
    d.getFullYear() !== year ||
    d.getMonth() !== monthIdx ||
    d.getDate() !== day
  ) {
    return null;
  }
  return startOfDay(d);
}

function parseExpiryDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return makeValidatedDate(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    let year = Number(m[3]);
    if (year < 100) year += 2000;
    return makeValidatedDate(year, Number(m[2]) - 1, Number(m[1]));
  }
  return null;
}

function daysBetween(a: Date, b: Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / 86400000);
}

function classify(daysUntil: number | null): ExpiryStatus {
  if (daysUntil === null) return "missing";
  if (daysUntil < 0) return "expired";
  if (daysUntil <= 30) return "critical";
  if (daysUntil <= 60) return "warn";
  if (daysUntil <= 90) return "info";
  return "ok";
}

function describeStatus(item: ExpiryItem): string {
  if (item.status === "missing") return "Kein Ablaufdatum hinterlegt";
  const d = item.daysUntilExpiry;
  if (d === null) return item.status === "ok" ? "Unbefristet" : "";
  if (d < 0) return `Vor ${-d} Tag(en) abgelaufen`;
  if (d === 0) return "Läuft heute ab";
  if (d === 1) return "Läuft morgen ab";
  return `Noch ${d} Tag(e)`;
}

// ───────────────────────────── Daten-Typen ─────────────────────────────────

type LicenseDetails = {
  identificationNumber?: string;
  expirationDate?: string;
  country?: string;
} | null;

type AllgemeinerlaubnisEntry = {
  bundesland?: string;
  expirationDate?: string;
};

type PilotProfile = {
  licenseA1A3?: boolean;
  licenseA2?: boolean;
  licenseSTS?: boolean;
  licenseA1A3Details?: LicenseDetails;
  licenseA2Details?: LicenseDetails;
  licenseSTSDetails?: LicenseDetails;
  allgemeinverfuegung?: AllgemeinerlaubnisEntry[];
};

type Drone = {
  id: string;
  name: string;
  alphaTangoId?: string | null;
  alphaTangoExpirationDate?: string | null;
  alphaTangoDocument?: unknown;
};

// ───────────────────────────── Sammeln ─────────────────────────────────────

function collectExpiryItems(drones: Drone[], pilot: PilotProfile | null): ExpiryItem[] {
  const now = new Date();
  const items: ExpiryItem[] = [];

  for (const d of drones) {
    if (!d.alphaTangoId && !d.alphaTangoDocument) continue;
    const exp = parseExpiryDate(d.alphaTangoExpirationDate);
    const days = exp ? daysBetween(now, exp) : null;
    items.push({
      id: `drone:${d.id}:alphaTango`,
      category: "alphaTango",
      label: `AlphaTango – ${d.name}`,
      subtitle: d.alphaTangoId ? `Nr. ${d.alphaTangoId}` : "Registrierung Frankreich",
      rawDate: d.alphaTangoExpirationDate ?? undefined,
      expiresAt: exp,
      daysUntilExpiry: days,
      status: classify(days),
    });
  }

  if (pilot) {
    const pushLicense = (key: "licenseA1A3" | "licenseA2" | "licenseSTS", label: string) => {
      if (!pilot[key]) return;
      const details =
        key === "licenseA1A3"
          ? pilot.licenseA1A3Details
          : key === "licenseA2"
            ? pilot.licenseA2Details
            : pilot.licenseSTSDetails;
      const exp = parseExpiryDate(details?.expirationDate);
      const days = exp ? daysBetween(now, exp) : null;
      items.push({
        id: `pilot:${key}`,
        category: "license",
        label,
        subtitle: details?.identificationNumber ? `eID ${details.identificationNumber}` : undefined,
        rawDate: details?.expirationDate,
        expiresAt: exp,
        daysUntilExpiry: days,
        status: classify(days),
      });
    };
    // In DE deckt das Fernpiloten-Zeugnis A2 die Kompetenz A1/A3 mit ab —
    // wir blenden A1/A3 dann aus, um keine zwei redundanten Ablauf-Einträge
    // für faktisch eine Lizenz zu zeigen. Sobald A2 in einem anderen Land
    // ausgestellt wurde (oder Land leer/unklar ist), wird A1/A3 wieder als
    // eigenständig ablaufende Lizenz getrackt.
    const a2Country = (pilot.licenseA2Details?.country || "DE").toUpperCase();
    const a2CoversA1A3 = pilot.licenseA2 && a2Country === "DE";
    if (!a2CoversA1A3) {
      pushLicense("licenseA1A3", "Kompetenznachweis A1/A3");
    }
    pushLicense("licenseA2", "Fernpiloten-Zeugnis A2");
    pushLicense("licenseSTS", "STS-Theorieprüfung");

    // Geografische Allgemeinerlaubnisse (§ 21h LuftVO) — eine pro Bundesland.
    // Eine AV ohne Ablaufdatum gilt als unbefristet → Status "ok" (nicht
    // "missing").
    const seenAv = new Set<string>();
    for (const e of pilot.allgemeinverfuegung || []) {
      if (!e || typeof e.bundesland !== "string") continue;
      const bl = e.bundesland.trim();
      if (!bl || seenAv.has(bl)) continue;
      seenAv.add(bl);
      const exp = parseExpiryDate(e.expirationDate);
      const days = exp ? daysBetween(now, exp) : null;
      items.push({
        id: `pilot:av:${bl}`,
        category: "allgemeinerlaubnis",
        label: `Allgemeinerlaubnis – ${bl}`,
        subtitle: "§ 21h LuftVO",
        rawDate: e.expirationDate,
        expiresAt: exp,
        daysUntilExpiry: days,
        status: exp ? classify(days) : "ok",
      });
    }
  }

  // Sortierung: kritisch zuerst, dann nach verbleibenden Tagen
  const order: Record<ExpiryStatus, number> = {
    expired: 0, critical: 1, warn: 2, info: 3, missing: 4, ok: 5,
  };
  items.sort((a, b) => {
    const o = order[a.status] - order[b.status];
    if (o !== 0) return o;
    const ad = a.daysUntilExpiry ?? 9999;
    const bd = b.daysUntilExpiry ?? 9999;
    return ad - bd;
  });
  return items;
}

function CategoryIcon({ category, color }: { category: ExpiryCategory; color: string }) {
  const props = { size: 22, color };
  switch (category) {
    case "alphaTango":
      return <Flag {...props} />;
    case "license":
      return <Award {...props} />;
    case "allgemeinerlaubnis":
      return <MapPin {...props} />;
  }
}

function targetFor(item: ExpiryItem): string {
  if (item.category === "alphaTango") {
    const droneId = item.id.split(":")[1];
    return `/drohnen/${droneId}/einrichten`;
  }
  return "/profil";
}

function ItemCard({ item }: { item: ExpiryItem }) {
  const color = STATUS_COLOR[item.status];
  return (
    <Link
      to={targetFor(item)}
      className="card flex items-center gap-3 hover:bg-[var(--color-app-bg-secondary)] transition"
      style={{ borderLeftColor: color, borderLeftWidth: 4 }}
      data-testid={`expiry-item-${item.id}`}
    >
      <span className="shrink-0">
        <CategoryIcon category={item.category} color={color} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold text-[var(--color-app-text)]">{item.label}</span>
        {item.subtitle ? (
          <span className="block text-xs text-[var(--color-app-text-tertiary)] mt-0.5">{item.subtitle}</span>
        ) : null}
        <span className="mt-1 flex flex-wrap items-center gap-2">
          <span
            className="rounded-md px-2 py-0.5 text-[11px] font-bold"
            style={{ backgroundColor: color + "22", color }}
          >
            {STATUS_LABEL[item.status]}
          </span>
          <span className="text-xs text-[var(--color-app-text-secondary)]">{describeStatus(item)}</span>
        </span>
        {item.rawDate ? (
          <span className="block text-[11px] text-[var(--color-app-text-tertiary)] mt-0.5">Gültig bis {item.rawDate}</span>
        ) : null}
      </span>
      <ChevronRight size={20} className="shrink-0 text-[var(--color-app-text-tertiary)]" />
    </Link>
  );
}

export default function ExpiryOverview() {
  const { data: drones = [], isLoading: dronesLoading } = useQuery<Drone[]>({
    queryKey: ["/api/drones"],
  });
  const { data: pilot = null, isLoading: pilotLoading } = useQuery<PilotProfile | null>({
    queryKey: ["/api/profile"],
  });

  const items = useMemo(() => collectExpiryItems(drones, pilot), [drones, pilot]);

  if (dronesLoading || pilotLoading) {
    return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;
  }

  const hasAny = items.length > 0;
  const upcoming = items.filter((i) => i.status !== "ok");
  const ok = items.filter((i) => i.status === "ok");

  return (
    <div className="space-y-4" data-testid="screen-expiry-overview">
      <p className="text-sm text-[var(--color-app-text-secondary)] leading-relaxed">
        Übersicht aller ablaufenden Dokumente. Du wirst in der mobilen App automatisch erinnert:
        <br />• 3 Monate vor Ablauf monatlich
        <br />• im vorletzten Monat wöchentlich
        <br />• im letzten Monat täglich
      </p>

      {!hasAny ? (
        <div className="card text-[var(--color-app-text-secondary)]">
          Keine Dokumente mit Ablaufdatum hinterlegt. Trage ein Datum bei deinen Lizenzen, der
          Versicherung oder der AlphaTango-Registrierung deiner Drohnen ein.
        </div>
      ) : null}

      {upcoming.length > 0 ? (
        <div className="space-y-2">
          <h2 className="text-xs font-bold tracking-wide text-[var(--color-app-text-secondary)]">AKTUELL ZU PRÜFEN</h2>
          {upcoming.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      ) : null}

      {ok.length > 0 ? (
        <div className="space-y-2 pt-2">
          <h2 className="text-xs font-bold tracking-wide text-[var(--color-app-text-secondary)]">GÜLTIG</h2>
          {ok.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
